import { Flex, Typography } from "antd";
import { RangePickerProps } from "antd/es/date-picker";
import { useCallback, useEffect, useRef, useState } from "react";
import { DefaultDateInput, DefaultRangeDateInput, EditableFile, CustomButton, CompanySelect } from "ui";
import { Dayjs } from "dayjs";
import { createReport, getReportById } from "services";
import { useNotificationContext } from "providers";
import { dateISOFormat, parseErrorMessage, ReportStorage } from "utils";
import { CompanyEntity, NOTIFICATION_TYPE_ENUM } from "interfaces";

import styles from "./reportCard.module.css";

type DateRange = [string, string] | [Dayjs | null, Dayjs | null] | undefined;

interface ReportCardProps {
  title: string;
  reportType: string;
  datePickerType?: "single" | "range" | "none";
  buttonText?: string;
  additionalParams?: Record<string, any>;
  customEndpoint?: string;
  vertical?: boolean;
  showTitle?: boolean;
  reactComponent?: React.ReactNode | string;
  companySelect?: boolean;
  selectedCompany?: CompanyEntity | null;
}

export const ReportCard: React.FC<ReportCardProps> = ({
  title,
  reportType,
  datePickerType = "single",
  buttonText = "Сформировать отчет",
  additionalParams,
  customEndpoint,
  vertical = false,
  showTitle = true,
  reactComponent,
  companySelect,
  selectedCompany,
}) => {
  const { openNotification } = useNotificationContext();
  const [date, setDate] = useState<string | DateRange>();
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string>();
  const [filename, setFilename] = useState("");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleSingleDateChange = (date: Dayjs | null, dateString: string) => {
    setDate(dateISOFormat(date, false));
    setDownloadUrl(undefined);
    setFilename("");
  };

  const handleRangeDateChange: RangePickerProps["onChange"] = (dates) => {
    if (Array.isArray(dates)) {
      setDate([dates[0], dates[1]]);
      setDownloadUrl(undefined);
      setFilename("");
    }
  };

  const clearPollingStatus = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    ReportStorage.remove(reportType);
    setLoading(false);
  }, [reportType]);

  const getFileById = useCallback(
    (id: string) => {
      if (intervalRef.current) clearInterval(intervalRef.current);

      intervalRef.current = setInterval(async () => {
        try {
          const data = await getReportById({ id });

          if (data.errorMessage) {
            openNotification({
              description: data.errorMessage,
              type: NOTIFICATION_TYPE_ENUM.ERROR,
            });
            clearPollingStatus();
          } else {
            if (data.doc) {
              setDownloadUrl(data.doc.url);
              setFilename(data.doc.docName);

              clearPollingStatus();
            }
          }
        } catch (e: any) {
          if (e.status !== 404) {
            clearPollingStatus();
            openNotification({
              description: parseErrorMessage(e),
              type: NOTIFICATION_TYPE_ENUM.ERROR,
            });
          }
        }
      }, 3000);

      ReportStorage.set(reportType, { id, isPolling: true });
    },
    [reportType, openNotification, clearPollingStatus]
  );

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setDownloadUrl(undefined);
      setFilename("");

      let params = {};
      if (datePickerType === "range" && Array.isArray(date)) {
        params = {
          startDate: dateISOFormat(date[0], false),
          endDate: dateISOFormat(date[1], false),
        };
      } else if (datePickerType === "single") {
        params = {
          startDate: dateISOFormat(date),
          endDate: date,
        };
      }

      const finalParams = { ...params, ...additionalParams };

      const reportId = await createReport(finalParams, reportType, customEndpoint);

      if (reportId) {
        getFileById(reportId);
      } else {
        clearPollingStatus();
        openNotification({
          description: "Ошибка при формировании отчета",
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      }
    } catch (e: any) {
      clearPollingStatus();
      openNotification({
        description: e.title || "Ошибка при формировании отчета",
        type: NOTIFICATION_TYPE_ENUM.ERROR,
      });
    }
  };

  useEffect(() => {
    const savedStatus = ReportStorage.get(reportType);
    if (savedStatus?.isPolling) {
      setLoading(true);
      getFileById(savedStatus.id);
    }
  }, [getFileById, reportType]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <Flex justify="space-between" vertical={vertical}>
      {showTitle && (
        <Flex align="center">
          <Typography.Text strong>{title}</Typography.Text>
        </Flex>
      )}
      <Flex vertical gap="large">
        <Flex align="center" gap="large">
          {reactComponent}
          <Flex className={styles.dateInputBlock}>
            {datePickerType === "single" && (
              <DefaultDateInput
                placeholder="Дата выписки"
                disabled={loading}
                onChange={handleSingleDateChange as (date: Dayjs | null, dateString: string | string[]) => void}
              />
            )}

            {datePickerType === "range" && (
              <DefaultRangeDateInput allowEmpty={[true, true]} onChange={handleRangeDateChange} disabled={loading} />
            )}
          </Flex>

          <Flex flex={1} justify="end">
            <CustomButton
              type="default"
              htmlType="submit"
              onClick={handleSubmit}
              loading={loading}
              disabled={datePickerType !== "none" && (!date || !date?.[0] || !date[1]) || (companySelect && !selectedCompany)}
              size="middle"
            >
              {buttonText}
            </CustomButton>
          </Flex>
        </Flex>
        {downloadUrl && filename && <EditableFile tooltip={filename} file={{ docName: filename, url: downloadUrl }} />}
      </Flex>
    </Flex>
  );
};
